import { useState, useEffect } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, Loading, Tag } from "@/components/common";
import { PlugsConnected, CheckCircle, WarningCircle, XCircle, Circle, Sparkle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS = {
  CONNECTED: { c: "#00FF9D", i: CheckCircle, t: "Connected" },
  TESTED: { c: "#00FF9D", i: CheckCircle, t: "Tested" },
  CONFIGURED: { c: "#00E5FF", i: CheckCircle, t: "Configured" },
  ERROR: { c: "#FF3366", i: XCircle, t: "Error" },
  NOT_CONFIGURED: { c: "#8A8A8A", i: Circle, t: "Not configured" },
};

export default function SetupCenter() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [testing, setTesting] = useState(null);

  const load = () => api.get("/integrations").then(({ data }) => setData(data)).catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  const test = async (id) => {
    setTesting(id);
    try { const { data: r } = await api.post(`/integrations/${id}/test`); toast[r.ok ? "success" : "message"](r.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(null); }
  };

  if (err) return <div className="p-6"><PageHeader icon={PlugsConnected} title="Setup Center" subtitle="Owner/Admin access required." /></div>;
  if (!data) return <Loading />;

  return (
    <div>
      <PageHeader icon={PlugsConnected} title="Infrastructure & Integrations"
        subtitle="Every external service MY CHOICE STUDIOS can use. Built-ins are live; the rest show exactly what's needed to connect. Secrets stay server-side." />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
        {["CONNECTED", "TESTED", "CONFIGURED", "ERROR", "NOT_CONFIGURED"].map((s) => (
          <Panel key={s} className="py-3 text-center">
            <div className="font-display text-2xl font-extrabold" style={{ color: STATUS[s].c }}>{data.counts[s] || 0}</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">{STATUS[s].t}</div>
          </Panel>
        ))}
      </div>

      {data.categories.map((cat) => {
        const rows = data.integrations.filter((i) => i.category === cat);
        if (!rows.length) return null;
        return (
          <div key={cat} className="mb-6">
            <h3 className="font-display text-lg font-bold mb-3 text-white">{cat}</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {rows.map((it) => {
                const S = STATUS[it.status] || STATUS.NOT_CONFIGURED;
                const Icon = S.i;
                return (
                  <Panel key={it.id} data-testid={`integration-${it.id}`} className="flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon size={18} weight="fill" style={{ color: S.c }} className="shrink-0" />
                        <span className="font-medium text-white truncate">{it.name}</span>
                      </div>
                      <span className="text-[10px] font-bold shrink-0" style={{ color: S.c }} data-testid={`status-${it.id}`}>{S.t.toUpperCase()}</span>
                    </div>
                    <p className="text-xs text-zinc-400">{it.purpose}</p>
                    <div className="flex flex-wrap gap-1.5 text-[10px]">
                      <Tag className={it.required ? "text-[#FFB800]" : "text-zinc-400"}>{it.required ? "Required" : "Optional"}</Tag>
                      <Tag className="text-zinc-400">{it.cost}</Tag>
                    </div>
                    {it.status === "NOT_CONFIGURED" && (
                      <div className="text-[11px] text-zinc-500 border-t border-white/5 pt-2">
                        <div><b className="text-zinc-400">Needs:</b> {it.creds?.join(", ")}</div>
                        <div><b className="text-zinc-400">Get it at:</b> {it.where}</div>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <button data-testid={`test-${it.id}`} onClick={() => test(it.id)} disabled={testing === it.id}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-white hover:border-white/30 disabled:opacity-60">
                        {testing === it.id ? <Spinner size={13} className="animate-spin" /> : <Sparkle size={13} />} Test Connection
                      </button>
                      {it.last_test && <span className="text-[10px] text-zinc-600">last: {String(it.last_test).slice(0, 10)}</span>}
                    </div>
                  </Panel>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
